var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import MinusIcon from '@heroicons/react/solid/MinusIcon.js';
import PlusIcon from '@heroicons/react/solid/PlusIcon.js';
import clsx from 'clsx';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Icon } from '../Icon/index.js';
import { ChevronDownIcon } from '../icons/index.js';
import { AccordionContext, useAccordionContext, } from './AccordionContext.js';
export const Accordion = (_a) => {
    var { as, children, className, variant = 'chevron', iconAlign = 'leading', inset = 'inset' } = _a, rest = __rest(_a, ["as", "children", "className", "variant", "iconAlign", "inset"]);
    const Component = as || 'div';
    return (React.createElement(Disclosure, Object.assign({ as: Component, className: clsx('space-y-1', [inset === 'flush' && '-ms-2 -me-2'], className) }, rest),
        React.createElement(AccordionContext.Provider, { value: { variant, iconAlign, inset } }, children)));
};
const AccordionButton = (_a) => {
    var { children, className } = _a, rest = __rest(_a, ["children", "className"]);
    const { variant, iconAlign } = useAccordionContext();
    const { backgroundType } = useBackgroundContext();
    const hoverClsx = [
        backgroundType === 'main' &&
            'text-content-on-background-subdued bg-background hover:bg-background-hovered hover:text-content-on-background-hovered',
        backgroundType === 'surface' &&
            'text-content-subdued bg-surface hover:bg-surface-hovered hover:text-content-hovered',
        backgroundType === 'secondary' &&
            'text-content-on-topbar bg-topbar hover:bg-topbar hover:text-content-on-topbar',
    ];
    const iconHoverClsx = [
        backgroundType === 'main' &&
            'group-hover:text-content-on-background-subdued',
        backgroundType === 'surface' && 'group-hover:text-content-hovered',
        backgroundType === 'secondary' && 'group-hover:text-content-on-topbar',
    ];
    const iconSizeClsx = [
        'h-5 w-5',
        [
            backgroundType === 'main' && 'text-content-on-background-subdued',
            backgroundType === 'surface' && 'text-content-subdued',
            backgroundType === 'secondary' && 'text-content-on-topbar',
        ],
        'transform transition-all ease-in-out duration-150',
    ];
    const icons = ({ open }) => {
        let icon;
        switch (variant) {
            case 'chevron':
                icon = (React.createElement(ChevronDownIcon, { className: clsx(iconHoverClsx, open ? '' : '-rotate-90 rtl:rotate-90') }));
                break;
            case 'plus':
                icon = !open ? (React.createElement(PlusIcon, { className: clsx(iconHoverClsx) })) : (React.createElement(MinusIcon, { className: clsx(iconHoverClsx) }));
                break;
            default:
                break;
        }
        if (iconAlign === 'leading') {
            return { leadingIcon: icon, trailingIcon: undefined };
        }
        return { leadingIcon: undefined, trailingIcon: icon };
    };
    return (React.createElement(Disclosure.Button, Object.assign({ className: clsx(hoverClsx, 'group w-full flex items-center text-start rounded-md', 'focus:outline-none focus-visible:ring ring-inset ring-offset-0 font-medium', 'py-2 px-2', className) }, rest), ({ open }) => {
        const { leadingIcon, trailingIcon } = icons({ open });
        return (React.createElement(React.Fragment, null,
            leadingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, 'flex-shrink-0 me-2') }, leadingIcon)),
            React.createElement("div", { className: "flex-grow truncate" }, children),
            trailingIcon && (React.createElement(Icon, { className: clsx(iconSizeClsx, 'flex-shrink-0 ms-2') }, trailingIcon))));
    }));
};
const AccordionPanel = props => {
    const { children, className } = props, rest = __rest(props, ["children", "className"]);
    const { text400Clsx } = useBackgroundContext();
    return (React.createElement(Transition, { enter: "transition duration-100 ease-out", enterFrom: "transform scale-95 opacity-0", enterTo: "transform scale-100 opacity-100", leave: "transition duration-75 ease-out", leaveFrom: "transform scale-100 opacity-100", leaveTo: "transform scale-95 opacity-0" },
        React.createElement(Disclosure.Panel, Object.assign({ className: clsx('w-full flex items-center text-start rounded-md', 'focus:outline-none focus-visible:ring', 'py-2 px-2') }, rest),
            React.createElement("div", { className: clsx('flex-1 min-w-0', text400Clsx, className) }, children))));
};
Accordion.Button = AccordionButton;
Accordion.Panel = AccordionPanel;
//# sourceMappingURL=Accordion.js.map